import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingTitle } from '@/components/ui/page-heading';
import type { AmlQuestionnairePurposeAndFunds } from '@/features/onboarding/aml-questionnaire/schema';
import { AML_FUNDS_ORIGIN_ANSWERS_WITH_OTHER, AML_OTHER_ANSWER, AML_PURPOSE_ANSWERS_WITH_OTHER } from '@/services/api/profile';

const AmlQuestionnaireFieldsetPurposeAndFundsLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('AMLQuestionnaire.purposeAndFunds.title')}</PageHeadingTitle>
      </legend>
    </PageHeading>
  );
};
AmlQuestionnaireFieldsetPurposeAndFundsLegend.displayName = 'AmlQuestionnaireFieldsetPurposeAndFundsLegend';

const AmlQuestionnaireFieldsetPurposeAndFunds = () => {
  const form = useFormContext<AmlQuestionnairePurposeAndFunds>();
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <FormField
        name="purpose"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('AMLQuestionnaire.purpose.title')}</FormLabel>
            <Select
              defaultValue={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                if (value !== AML_OTHER_ANSWER && form.getValues('purposeOther')) form.setValue('purposeOther', '');
              }}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('AMLQuestionnaire.pleaseSpecify')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {AML_PURPOSE_ANSWERS_WITH_OTHER.map((answer) => (
                  <SelectItem key={answer} value={answer}>
                    {t(`AMLQuestionnaire.purpose.options.${answer}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      {form.watch('purpose') === AML_OTHER_ANSWER ? (
        <FormField
          name="purposeOther"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{`${t('AMLQuestionnaire.pleaseSpecify')} (${t('AMLQuestionnaire.purpose.title')})`}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t('AMLQuestionnaire.pleaseSpecify')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}

      <FormField
        name="fundsOrigin"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('AMLQuestionnaire.fundsOrigin.title')}</FormLabel>
            <Select
              defaultValue={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                if (value !== AML_OTHER_ANSWER && form.getValues('fundsOriginOther')) form.setValue('fundsOriginOther', '');
              }}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('AMLQuestionnaire.pleaseSpecify')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {AML_FUNDS_ORIGIN_ANSWERS_WITH_OTHER.map((answer) => (
                  <SelectItem key={answer} value={answer}>
                    {t(`AMLQuestionnaire.fundsOrigin.options.${answer}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      {form.watch('fundsOrigin') === AML_OTHER_ANSWER ? (
        <FormField
          name="fundsOriginOther"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{`${t('AMLQuestionnaire.pleaseSpecify')} (${t('AMLQuestionnaire.fundsOrigin.title')})`}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t('AMLQuestionnaire.pleaseSpecify')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </div>
  );
};
AmlQuestionnaireFieldsetPurposeAndFunds.displayName = 'AmlQuestionnaireFieldsetPurposeAndFunds';

export { AmlQuestionnaireFieldsetPurposeAndFunds, AmlQuestionnaireFieldsetPurposeAndFundsLegend };
